import { LOCALE_COOKIE_MAX_AGE_SECONDS, routing, type AppLocale } from '@/i18n/routing'

/** Display metadata for each supported locale, used by the switcher and the
 * language intro modal. `label` is the language's own name (kept verbatim by
 * convention). `ariaLabel` is the English name, suitable for English screen
 * readers and for sentence-substitution like "Martini in {language}". */
export const LOCALE_DISPLAY: Record<AppLocale, { label: string; ariaLabel: string }> = {
  en: { label: 'English', ariaLabel: 'English' },
  ja: { label: '日本語', ariaLabel: 'Japanese' },
  es: { label: 'Español', ariaLabel: 'Spanish' },
  ar: { label: 'العربية', ariaLabel: 'Arabic' },
  ko: { label: '한국어', ariaLabel: 'Korean' },
}

/**
 * Locales rendered right-to-left. Future RTL languages (Hebrew, Persian, Urdu)
 * get RTL behavior for free by being added here.
 */
export const RTL_LOCALES: readonly AppLocale[] = ['ar']

/**
 * Map a locale to its base text direction. Drives `<html dir>` and any
 * direction-sensitive logic that needs to know whether the current locale is RTL.
 */
export function getLocaleDirection(locale: AppLocale): 'ltr' | 'rtl' {
  return RTL_LOCALES.includes(locale) ? 'rtl' : 'ltr'
}

/**
 * Map app locale → Open Graph locale string (e.g. `ja` → `ja_JP`). Used by
 * `generateMetadata` in marketing/auth surfaces. Defaults to `en_US` for any
 * unknown locale so we never emit an invalid `og:locale` tag.
 */
const OG_LOCALES: Record<AppLocale, string> = {
  en: 'en_US',
  ja: 'ja_JP',
  es: 'es_ES',
  ar: 'ar_AR',
  ko: 'ko_KR',
}

export function getOpenGraphLocale(locale: string): string {
  return OG_LOCALES[locale as AppLocale] ?? 'en_US'
}

/**
 * Persist the user's explicit locale choice in a long-lived `NEXT_LOCALE` cookie.
 * With `localePrefix: 'as-needed'`, this cookie is what makes the choice stick
 * across full page loads. On app surfaces (no URL locale prefix) it is also the
 * primary input to `resolveAppLocale()` server-side.
 */
export function persistLocaleChoice(locale: AppLocale): void {
  if (typeof document === 'undefined') return
  const secure = window.location.protocol === 'https:' ? '; secure' : ''
  // This preference is intentionally script-readable: the OAuth and locale
  // flows read it client-side. It contains no identity, token, or session data.
  document.cookie = `NEXT_LOCALE=${locale}; path=/; max-age=${LOCALE_COOKIE_MAX_AGE_SECONDS}; samesite=lax${secure}`
}

/** Map a timezone hint to the locale we should suggest. Lets us catch users
 * whose `navigator.languages` is misconfigured but whose OS clock proves they
 * are in a target region. Add new locales here when expanding. */
const TIMEZONE_LOCALE_HINTS: Record<string, Exclude<AppLocale, 'en'>> = {
  'Asia/Tokyo': 'ja',
  'Asia/Seoul': 'ko',
  'Asia/Riyadh': 'ar',
  'Asia/Dubai': 'ar',
  'Asia/Qatar': 'ar',
  'Asia/Kuwait': 'ar',
  'Asia/Bahrain': 'ar',
  'Asia/Muscat': 'ar',
  'Asia/Baghdad': 'ar',
  'Asia/Amman': 'ar',
  'Asia/Beirut': 'ar',
  'Asia/Damascus': 'ar',
  'Africa/Cairo': 'ar',
  'Africa/Algiers': 'ar',
  'Africa/Tunis': 'ar',
  'Africa/Casablanca': 'ar',
  'Africa/Tripoli': 'ar',
  'Africa/Khartoum': 'ar',
}

/**
 * Read the `NEXT_LOCALE` cookie value, or null if absent/invalid.
 */
export function readLocaleCookie(): AppLocale | null {
  if (typeof document === 'undefined') return null
  const value = document.cookie
    .split('; ')
    .find(row => row.startsWith('NEXT_LOCALE='))
    ?.split('=')[1]
  return (routing.locales as readonly string[]).includes(value ?? '') ? (value as AppLocale) : null
}

/**
 * Heuristic for "should we suggest a non-English locale to this user?".
 * Returns the suggested locale, or null if English is the right default.
 *
 * Priority: explicit browser language list first (user changed it on purpose),
 * then OS timezone as a fallback for users on default browser configs.
 */
export function prefersNonEnglishLocale(): AppLocale | null {
  if (typeof navigator === 'undefined') return null

  const supported = routing.locales as readonly AppLocale[]
  const nonEnglish = supported.filter(l => l !== 'en')

  const languages = navigator.languages && navigator.languages.length > 0 ? navigator.languages : [navigator.language]
  for (const lang of languages) {
    const prefix = lang?.toLowerCase().split('-')[0]
    const match = nonEnglish.find(l => l === prefix)
    if (match) return match
  }

  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone
    const hint = TIMEZONE_LOCALE_HINTS[tz]
    if (hint && nonEnglish.includes(hint)) return hint
  } catch {
    // Intl unavailable — fall through.
  }

  return null
}
